import React from "react";
import { PanelTopClose, PanelTopOpen, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SimpleColumnManager } from "@/components/shared/SimpleColumnManager";
import { TableCalendarButton } from "../TableCalendarButton";
import { useSimpleTableContext } from "../../contexts/SimpleTableContext";
import { useFilterContext } from "@/contexts/FilterContext";
import {
  useActiveFilterCount,
  useHasActiveSearch,
  useFilterActions,
} from "./SimpleTableHeaderHooks";
import { SimpleTableHeaderSearchBar } from "./SimpleTableHeaderSearchBar";
import { SimpleTableHeaderFilterControls } from "./SimpleTableHeaderFilterControls";
import { SimpleTableHeaderActiveFilters } from "./SimpleTableHeaderActiveFilters";
import { getQuickFilterFields } from "../../utils/filter-rendering";
import type { BaseEntity, SimpleTableConfig, FieldValue } from "../../types";
import type { GroupableField, GroupingContext } from "../../../GroupingWrapper/types";

interface AdvancedFilterProps {
  advancedFilters:
    | Record<string, unknown>
    | Array<{ id: string; field: string; operator: string; value: unknown }>;
  filterOperator: "AND" | "OR";
  setFilterOperator: (operator: "AND" | "OR") => void;
  addAdvancedFilter: (fieldId: string, filterType: string, value: FieldValue) => void;
  removeAdvancedFilter: (fieldId: string) => void;
  clearAllAdvancedFilters: () => void;
}

interface SimpleTableHeaderProps<TData extends BaseEntity> {
  config: SimpleTableConfig<TData>;
  selectedItems: Set<string>;
  sortColumns: Array<{ field: string; direction: "asc" | "desc"; priority: number }>;
  removeSortColumn: (field: string) => void;
  clearAllSorting: () => void;
  handleBulkAction: (actionId: string) => void;
  resetColumnWidthsCallback: () => void;
  paginatedData: TData[];
  advancedFilterProps: AdvancedFilterProps;
  groupingContext?: GroupingContext;
  groupableFields?: GroupableField[];
}

export function SimpleTableHeader<TData extends BaseEntity>({
  config,
  selectedItems,
  sortColumns,
  removeSortColumn,
  clearAllSorting,
  handleBulkAction,
  resetColumnWidthsCallback,
  paginatedData,
  advancedFilterProps,
  groupingContext,
  groupableFields,
}: SimpleTableHeaderProps<TData>) {
  // Get context for savedViewsControls and configSavedViews
  const context = useSimpleTableContext<TData>();
  const { savedViewsControls, openModal } = context;

  // Get FilterContext for filter state and actions
  const filterContext = useFilterContext<TData>();
  const { filters, actions, uiState } = filterContext;

  // Use custom hooks for filter logic
  const activeFilterCount = useActiveFilterCount<TData>();
  const hasActiveSearch = useHasActiveSearch<TData>();
  const { handleClearAll, handleRemoveFilter } = useFilterActions<TData>();

  // Desktop: allow collapsing the whole filter/view toolbar so the table data
  // gets maximum vertical space. Persisted per-entity (transient device-local
  // UI state — not server-backed, see ONE_TIME_UI_STATE_PER_USER scope note).
  const collapseStorageKey = `crm-table-header-collapsed:${config.entity}`;
  const [isCollapsed, setIsCollapsed] = React.useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(collapseStorageKey) === "1";
  });

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(collapseStorageKey, isCollapsed ? "1" : "0");
  }, [collapseStorageKey, isCollapsed]);

  // Collapsed state: render only a thin accordion strip with an expand control
  // and a summary of any active filters/search, hiding the column manager,
  // calendar button, search bar, and filter controls.
  if (isCollapsed) {
    const collapsedSummaryCount = activeFilterCount + (hasActiveSearch ? 1 : 0);
    return (
      <div className="border-b border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-900/50">
        <div className="px-3 py-1 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Filter className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden />
            <span className="text-xs font-medium text-muted-foreground truncate">
              Filters &amp; view options
            </span>
            {collapsedSummaryCount > 0 && (
              <Badge variant="secondary" className="h-5 px-2 text-xs font-medium">
                {collapsedSummaryCount} active
              </Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-muted-foreground"
            title="Expand filters & view options"
            aria-expanded={false}
            aria-label="Expand filters and view options"
            onClick={() => setIsCollapsed(false)}
          >
            <PanelTopOpen className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header Controls */}
      <div className="border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
        {/* Main Search and Filter Controls */}
        <div className="px-3 py-2 space-y-1.5">
          {/* Primary Row: Views + Search + Filters + Grouping + More */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            {/* Left: Saved Views Controls */}
            {savedViewsControls && <div className="flex-shrink-0">{savedViewsControls}</div>}

            {/* Center: Search Bar */}
            {config.features?.search && (
              <SimpleTableHeaderSearchBar
                entity={config.entity}
                searchValue={filters.search}
                onSearchChange={actions.setSearchTerm}
              />
            )}

            {/* Right: Filter Control Buttons + Grouping + More */}
            <SimpleTableHeaderFilterControls
              config={config}
              activeFilterCount={activeFilterCount}
              onClearAll={handleClearAll}
              sortColumns={sortColumns}
              onRemoveSortColumn={removeSortColumn}
              onClearAllSorting={clearAllSorting}
              onResetColumnWidths={resetColumnWidthsCallback}
              advancedFilterCount={filters.advanced.length}
              groupableFields={groupableFields}
              groupingContext={groupingContext}
              quickFiltersPopoverProps={
                config.features?.filters
                  ? {
                      simpleFilters: filters.simple,
                      onFiltersChange: (newFilters) => {
                        const quickFields = getQuickFilterFields<TData>(config);
                        quickFields.forEach(({ id }) => {
                          actions.setSimpleFilter(id, newFilters[id]);
                        });
                      },
                      onCustomRangeOpen: (fieldId, fieldLabel) => {
                        actions.setCustomRangeModal({
                          isOpen: true,
                          fieldId,
                          fieldLabel,
                        });
                      },
                      advancedFilterProps,
                    }
                  : undefined
              }
            />
          </div>

          {/* Active Filters Row - Chips showing current filters */}
          {(activeFilterCount > 0 || hasActiveSearch) && (
            <SimpleTableHeaderActiveFilters
              config={config}
              searchValue={filters.search}
              simpleFilters={filters.simple}
              advancedFilters={filters.advanced}
              onRemoveSearch={() => actions.setSearchTerm("")}
              onRemoveFilter={handleRemoveFilter}
              onRemoveAdvancedFilter={advancedFilterProps.removeAdvancedFilter}
            />
          )}
        </div>

        {/* Actions Toolbar - Consolidated */}
        <div className="px-3 py-1.5 bg-gray-50/50 border-t border-gray-200 dark:bg-gray-900/50 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            {/* Left side - View Controls */}
            <div className="flex items-center gap-2 flex-wrap">
              {config.features?.columnVisibility && <SimpleColumnManager />}

              <TableCalendarButton<TData>
                config={config}
                data={paginatedData}
                onOpen={() => openModal?.(`calendar-view-${config.entity}`)}
              />
            </div>

            {/* Right side - Action Buttons */}
            {/* Bulk Actions button removed - using floating GroupedBulkActions bar instead */}
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-muted-foreground"
                title="Collapse filters & view options"
                aria-expanded={true}
                aria-label="Collapse filters and view options"
                onClick={() => setIsCollapsed(true)}
              >
                <PanelTopClose className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
